import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Layout,
  ConfigProvider,
  Button,
  type MenuProps,
  Dropdown,
  Avatar,
  Breadcrumb,
  type BreadcrumbProps,
  Modal,
} from "antd";
import {
  UserOutlined,
  MenuOutlined,
  CaretDownFilled,
  LogoutOutlined,
} from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { useUserInfo } from "../../hooks/useUserInfo";
import "./index.css";
import AdminSider from "./sider";
import adminLogo from "../../assets/admin-logo.svg";
import scgLogo from "../../assets/scg.png";

const { Header, Content } = Layout;

interface AdminLayoutProps {
  children: React.ReactNode;
  breadcrumb?: BreadcrumbProps;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, breadcrumb }) => {
  const [collapsed, setCollapsed] = useState(false); // For desktop Sider
  const [drawerVisible, setDrawerVisible] = useState(false); // For mobile Drawer
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const { instance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  
  // User info hook
  const { userInfo, isLoading, isAdmin } = useUserInfo();

  // Check if current path is error page
  const isErrorPage = location.pathname === '/error';

  // Role-based access control for admin pages
  useEffect(() => {
    // Only check for admin pages (not error page)
    if (!isErrorPage && location.pathname.startsWith('/admin') && !isLoading) {
      if (userInfo && !isAdmin) {
        // Check if user has content management roles
        const hasContentRole = userInfo.roles?.some(role => 
          role === 'content-owner' || role === 'content-member'
        );
        
        // User is logged in but doesn't have admin privileges or content management roles
        if (!hasContentRole) {
          navigate('/error', { replace: true });
        }
      }
    }
  }, [location.pathname, userInfo, isAdmin, isLoading, isErrorPage, navigate]);

  const toggleDrawer = () => setDrawerVisible(!drawerVisible);

  const handleLogoutClick = () => {
    setLogoutModalVisible(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      setLogoutModalVisible(false);
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutModalVisible(false);
  };

  const profileItems: MenuProps['items'] = [
    {
      key: 'profile-header',
      label: (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '8px 0',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: '8px',
          cursor: 'default',
          pointerEvents: 'none'
        }}>
          <Avatar 
            size={40} 
            icon={<UserOutlined />}
          />
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px'
          }}>
            <span style={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#000000',
              lineHeight: '1.2'
            }}>
              {userInfo?.name || 'Loading...'}
            </span>
            <span style={{
              fontSize: '12px',
              color: '#666666',
              lineHeight: '1.2'
            }}>
              {userInfo?.roles?.join(', ') || 'No roles assigned'}
            </span>
          </div>
        </div>
      ),
      disabled: true,
      style: {
        cursor: 'default',
        backgroundColor: 'transparent'
      }
    },
    {
      key: 'logout',
      danger: true,
      label: 'ออกจากระบบ',
      icon: <LogoutOutlined />,
      onClick: handleLogoutClick,
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "rgba(250, 140, 22, 1)",
          borderRadius: 4,
          fontFamily: "SCG Regular",
          colorBgContainer: "#ffffff",
          colorText: "#000000",
          colorTextSecondary: "#666666",
        },
        components: {
          Layout: {
            headerBg: "#ffffffff",
            headerPadding: "0",
            headerHeight: "48px",
            siderBg: "#1f2937",
            bodyBg: "rgba(245, 245, 245, 1)",
          },

          Menu: {
            itemBg: "#ffffffff",
            itemColor: "#000000ff",
            itemHoverBg: "rgba(255, 241, 184, 0.5)",
            itemSelectedBg: "rgba(255, 241, 184, 0.5)",
            itemSelectedColor: "rgba(250, 140, 22, 1)",
          },
          Button: {
            defaultBg: "#ffffff",
            defaultColor: "#000000",
            defaultBorderColor: "#d9d9d9",
          },
          Card: {
            colorBgContainer: "#ffffff",
            colorBorderSecondary: "#f0f0f0",
            colorText: "#000000",
          },
          Table: {
            colorBgContainer: "#ffffff",
            colorText: "#000000",
            headerBg: "#fafafa",
          },
        },
      }}
    >
      <Layout style={{ minHeight: "100vh" }}>
        <Header className="header">
          <div className="header-logo">
            {/* <img src={logo} alt="logo" /> */}
          </div>
          {!isErrorPage && (
            <Button
              className="collapse-button"
              type="text"
              icon={collapsed ? <MenuOutlined /> : <MenuOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ marginRight: 16 }}
            />
          )}
          <img src={adminLogo} alt="Admin Logo" style={{ height: '14px', marginRight: '8px' }} />
          <img src={scgLogo} alt="SCG Logo" style={{ height: '14px', marginRight: '8px' }} />
          <div className="header-title">DX Blueprint</div>
          {!isErrorPage && (
            <div className="header-actions">
              <Dropdown className="header-action-dropdown" menu={{ items: profileItems }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar 
                      size={22} 
                      icon={<UserOutlined />}
                    />
                    <span>{userInfo?.name || 'Loading...'}</span>
                  </div>
                  <CaretDownFilled />
                </div>
              </Dropdown>
              {/* Mobile Sider Toggle */}
              <Button
                className="header-action-btn"
                id="mobile-sider-toggle"
                icon={<MenuOutlined />}
                onClick={toggleDrawer}
              />
            </div>
          )}
        </Header>
        <Layout>
          {!isErrorPage && (
            <AdminSider
              collapsed={collapsed}
              drawerVisible={drawerVisible}
              setDrawerVisible={setDrawerVisible}
            />
          )}
          <Layout
            style={{
              padding: isErrorPage ? "0" : "0 24px 24px",
              overflow: "hidden",
              width: "100%",
            }}
          >
            {breadcrumb && !isErrorPage && (
              <Breadcrumb {...breadcrumb} style={{ paddingTop: 16 }} />
            )}
            {isErrorPage ? (
              // For error page, render children directly without Content wrapper
              children
            ) : (
              <Content
                style={{
                  background: "#fff",
                  padding: 24,
                  margin: "16px 0",
                  minHeight: 280,
                  borderRadius: 8,
                  overflow: "initial",
                }}
              >
                {children}
              </Content>
            )}
          </Layout>
        </Layout>

        <Modal
          title={
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: '#000000',
              fontSize: '16px',
              fontWeight: 500
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#fff2e8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <LogoutOutlined style={{
                  color: 'rgba(250, 140, 22, 1)',
                  fontSize: '20px'
                }} />
              </div>
              <span>ยืนยันการออกจากระบบ</span>
            </div>
          }
          open={logoutModalVisible}
          onOk={handleLogoutConfirm}
          onCancel={handleLogoutCancel}
          okText="ออกจากระบบ"
          cancelText="ยกเลิก"
          centered
          width={420}
          okButtonProps={{
            danger: true,
            size: 'large',
            style: {
              borderRadius: '6px',
              fontWeight: 500,
              height: '40px',
              minWidth: '100px'
            }
          }}
          cancelButtonProps={{
            size: 'large',
            style: {
              borderRadius: '6px',
              fontWeight: 500,
              height: '40px',
              minWidth: '100px'
            }
          }}
          styles={{
            header: {
              paddingBottom: '20px',
              borderBottom: '1px solid #f0f0f0'
            },
            body: {
              paddingTop: '24px',
              paddingBottom: '24px'
            },
            footer: {
              paddingTop: '20px',
              borderTop: '1px solid #f0f0f0'
            }
          }}
        >
          <div style={{
            color: '#666666',
            fontSize: '14px',
            lineHeight: '1.6',
            padding: '0 52px'
          }}>
            <p style={{ margin: 0, textAlign: 'center' }}>
              คุณต้องการออกจากระบบหรือไม่?
            </p>
            <p style={{
              margin: '8px 0 0 0',
              textAlign: 'center',
              fontSize: '13px',
              color: '#999999'
            }}>
              การออกจากระบบจะนำคุณกลับไปยังหน้าแรก
            </p>
          </div>
        </Modal>
      </Layout>
    </ConfigProvider>
  );
};

export default AdminLayout;
